import React, { useState } from 'react';
import { 
  Zap, 
  Atom, 
  ArrowRight, 
  Battery, 
  CircleDot,
  Layers,
  RefreshCw,
  Shield,
  Gauge,
  ThermometerSun
} from 'lucide-react';

// Multi-Ion Battery Cell Illustration Component
const MultiIonIllustration = () => {
  return (
    <div className="relative w-full max-w-xl mx-auto">
      <svg viewBox="0 0 500 300" className="w-full h-auto">
        {/* Background gradient */}
        <defs>
          <linearGradient id="cellGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#064e3b" />
            <stop offset="50%" stopColor="#0f172a" />
            <stop offset="100%" stopColor="#164e63" />
          </linearGradient>
          <linearGradient id="electrodeLeft" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="electrodeRight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0891b2" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        
        {/* Cell body */}
        <rect x="50" y="50" width="400" height="200" rx="20" fill="url(#cellGradient)" stroke="#374151" strokeWidth="2"/>
        
        {/* Left Electrode (Anode) */}
        <rect x="70" y="70" width="40" height="160" rx="5" fill="url(#electrodeLeft)" opacity="0.9"/>
        <text x="90" y="260" textAnchor="middle" fill="#9ca3af" fontSize="12" fontWeight="500">Anode</text>
        
        {/* Right Electrode (Cathode) */}
        <rect x="390" y="70" width="40" height="160" rx="5" fill="url(#electrodeRight)" opacity="0.9"/>
        <text x="410" y="260" textAnchor="middle" fill="#9ca3af" fontSize="12" fontWeight="500">Cathode</text>
        
        {/* Electrolyte area label */}
        <text x="250" y="260" textAnchor="middle" fill="#6b7280" fontSize="11">Electrolyte</text>
        
        {/* Na+ Ions (Sodium) - Yellow/Orange */}
        <g className="animate-pulse">
          <circle cx="150" cy="100" r="18" fill="#f59e0b" opacity="0.9">
            <animate attributeName="cx" values="150;320;150" dur="4s" repeatCount="indefinite"/>
          </circle>
          <text x="150" y="105" textAnchor="middle" fill="#1f2937" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="150;320;150" dur="4s" repeatCount="indefinite"/>
            Na⁺
          </text>
        </g>
        
        <g className="animate-pulse" style={{animationDelay: '0.5s'}}>
          <circle cx="180" cy="150" r="18" fill="#f59e0b" opacity="0.9">
            <animate attributeName="cx" values="180;340;180" dur="4.5s" repeatCount="indefinite"/>
          </circle>
          <text x="180" y="155" textAnchor="middle" fill="#1f2937" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="180;340;180" dur="4.5s" repeatCount="indefinite"/>
            Na⁺
          </text>
        </g>
        
        <g className="animate-pulse" style={{animationDelay: '1s'}}>
          <circle cx="160" cy="200" r="18" fill="#f59e0b" opacity="0.9">
            <animate attributeName="cx" values="160;330;160" dur="5s" repeatCount="indefinite"/>
          </circle>
          <text x="160" y="205" textAnchor="middle" fill="#1f2937" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="160;330;160" dur="5s" repeatCount="indefinite"/>
            Na⁺
          </text>
        </g>
        
        {/* K+ Ions (Potassium) - Purple */}
        <g className="animate-pulse" style={{animationDelay: '0.3s'}}>
          <circle cx="200" cy="120" r="20" fill="#8b5cf6" opacity="0.9">
            <animate attributeName="cx" values="200;350;200" dur="3.5s" repeatCount="indefinite"/>
          </circle>
          <text x="200" y="125" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="200;350;200" dur="3.5s" repeatCount="indefinite"/>
            K⁺
          </text>
        </g>
        
        <g className="animate-pulse" style={{animationDelay: '0.8s'}}>
          <circle cx="220" cy="175" r="20" fill="#8b5cf6" opacity="0.9">
            <animate attributeName="cx" values="220;360;220" dur="4s" repeatCount="indefinite"/>
          </circle>
          <text x="220" y="180" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="220;360;220" dur="4s" repeatCount="indefinite"/>
            K⁺
          </text>
        </g>
        
        <g className="animate-pulse" style={{animationDelay: '1.2s'}}>
          <circle cx="190" cy="220" r="20" fill="#8b5cf6" opacity="0.9">
            <animate attributeName="cx" values="190;340;190" dur="4.2s" repeatCount="indefinite"/>
          </circle>
          <text x="190" y="225" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="bold">
            <animate attributeName="x" values="190;340;190" dur="4.2s" repeatCount="indefinite"/>
            K⁺
          </text>
        </g>
        
        {/* Direction arrows */}
        <path d="M 130 150 L 370 150" stroke="#4b5563" strokeWidth="1" strokeDasharray="5,5" opacity="0.5"/>
        <polygon points="375,150 365,145 365,155" fill="#4b5563" opacity="0.5"/>
        
        {/* Labels for ions */}
        <g>
          <rect x="130" y="15" width="80" height="25" rx="12" fill="#f59e0b" opacity="0.2"/>
          <circle cx="145" cy="27" r="8" fill="#f59e0b"/>
          <text x="148" y="31" textAnchor="middle" fill="#1f2937" fontSize="8" fontWeight="bold">Na⁺</text>
          <text x="185" y="31" fill="#f59e0b" fontSize="11" fontWeight="500">Sodium</text>
        </g>
        
        <g>
          <rect x="280" y="15" width="95" height="25" rx="12" fill="#8b5cf6" opacity="0.2"/>
          <circle cx="297" cy="27" r="9" fill="#8b5cf6"/>
          <text x="297" y="31" textAnchor="middle" fill="#ffffff" fontSize="8" fontWeight="bold">K⁺</text>
          <text x="345" y="31" fill="#8b5cf6" fontSize="11" fontWeight="500">Potassium</text>
        </g>
      </svg>
      
      {/* Caption */}
      <p className="text-center text-gray-500 text-sm mt-4">
        Multi-Ion technology uses Na⁺ and K⁺ ions for faster charge transfer
      </p>
    </div>
  );
};

const HowMultiIonWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 0,
      title: 'Multi-Ion Chemistry',
      subtitle: 'The Foundation',
      icon: Atom,
      description: 'Unlike lithium-ion batteries that rely on a single ion carrier, Multi-Ion technology uses multiple ion species working in harmony. This enables faster ion transport and more efficient charge storage.',
      keyPoints: [
        'Multiple ion carriers for faster transport',
        'Proprietary electrolyte formulation',
        'Advanced electrode materials'
      ],
      color: 'emerald'
    },
    {
      id: 1,
      title: 'Rapid Charging',
      subtitle: '15-Minute Full Charge',
      icon: Zap,
      description: 'The multi-ion architecture creates wider pathways for ion movement, dramatically reducing internal resistance. This allows sustained high-current charging without degradation.',
      keyPoints: [
        'Low internal resistance design',
        'Sustained 4C+ charge rates',
        'No fast-charge penalty on cycle life'
      ],
      color: 'cyan'
    },
    {
      id: 2,
      title: 'Extended Cycle Life',
      subtitle: '5,000+ Cycles',
      icon: RefreshCw,
      description: 'Traditional batteries degrade due to structural changes during cycling. Our stable crystal structure maintains integrity through thousands of charge-discharge cycles.',
      keyPoints: [
        'Stable electrode structure',
        'Minimal capacity fade',
        '3x longer than lithium-ion'
      ],
      color: 'amber'
    },
    {
      id: 3,
      title: 'Temperature Tolerance',
      subtitle: '-40°C to 65°C',
      icon: ThermometerSun,
      description: 'Multi-Ion chemistry remains stable across extreme temperatures where lithium-ion fails. No heating in cold or cooling in heat — just reliable performance.',
      keyPoints: [
        'Operates in freezers and hot environments',
        'No thermal management needed',
        'Consistent power delivery'
      ],
      color: 'emerald'
    },
    {
      id: 4,
      title: 'Inherent Safety',
      subtitle: 'Stable by Design',
      icon: Shield,
      description: 'The chemistry is inherently stable with no dendrite formation risk. This eliminates internal short circuits and makes the cells safe for enclosed industrial spaces.',
      keyPoints: [
        'No dendrite formation',
        'Stable under abuse conditions',
        'UN 38.3 certified'
      ],
      color: 'cyan'
    }
  ];

  const advantages = [
    { icon: Gauge, label: '15 min', desc: 'Full Charge' },
    { icon: RefreshCw, label: '5,000+', desc: 'Cycles' },
    { icon: ThermometerSun, label: '-40°C to 65°C', desc: 'Range' },
    { icon: Shield, label: 'UN 38.3', desc: 'Certified' }
  ];

  return (
    <section id="how-it-works" className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 overflow-hidden" data-testid="how-multiion-works-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
            <Atom className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-xs sm:text-sm font-semibold uppercase tracking-wider">Technology</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            How <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Multi-Ion</span> Works
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
            A revolutionary approach to energy storage that delivers performance lithium-ion cannot match.
          </p>
        </div>

        {/* Key Advantages Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {advantages.map((adv, index) => (
            <div key={index} className="flex items-center gap-3 p-4 bg-gray-800/30 rounded-xl border border-white/10">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <adv.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-bold text-lg leading-tight">{adv.label}</p>
                <p className="text-gray-500 text-xs">{adv.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Process Steps - Interactive */}
        <div className="grid lg:grid-cols-5 gap-2 mb-8">
          {processSteps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(index)}
              className={`p-4 rounded-xl text-left transition-all duration-300 ${
                activeStep === index 
                  ? `bg-${step.color}-500/20 border-2 border-${step.color}-500/50` 
                  : 'bg-gray-800/30 border border-white/10 hover:border-white/20'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg mb-2 flex items-center justify-center ${
                activeStep === index ? `bg-${step.color}-500/30` : 'bg-white/5'
              }`}>
                <step.icon className={`w-4 h-4 ${activeStep === index ? `text-${step.color}-400` : 'text-gray-500'}`} />
              </div>
              <p className={`font-semibold text-sm ${activeStep === index ? 'text-white' : 'text-gray-400'}`}>
                {step.title}
              </p>
              <p className={`text-xs ${activeStep === index ? `text-${step.color}-400` : 'text-gray-600'}`}>
                {step.subtitle}
              </p>
            </button>
          ))}
        </div>

        {/* Active Step Detail */}
        <div className="bg-gray-800/30 rounded-2xl border border-white/10 p-6 sm:p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left - Description */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-${processSteps[activeStep].color}-500/20 flex items-center justify-center`}>
                  {React.createElement(processSteps[activeStep].icon, { 
                    className: `w-6 h-6 text-${processSteps[activeStep].color}-400` 
                  })}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{processSteps[activeStep].title}</h3>
                  <p className={`text-${processSteps[activeStep].color}-400 text-sm`}>{processSteps[activeStep].subtitle}</p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                {processSteps[activeStep].description}
              </p>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
              >
                Learn more from our engineers
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Right - Key Points */}
            <div className="space-y-3">
              <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-4">Key Features</p>
              {processSteps[activeStep].keyPoints.map((point, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className={`w-8 h-8 rounded-full bg-${processSteps[activeStep].color}-500/20 flex items-center justify-center flex-shrink-0`}>
                    <CircleDot className={`w-4 h-4 text-${processSteps[activeStep].color}-400`} />
                  </div>
                  <p className="text-white text-sm font-medium">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Visual Comparison */}
        <div className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-gray-800/50 to-gray-800/30 rounded-2xl border border-white/10">
          <h3 className="text-lg font-bold text-white mb-6 text-center">Multi-Ion vs Lithium-Ion at a Glance</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { metric: 'Charge Time', lithium: '4-8 hours', multiIon: '15 minutes', winner: 'multiIon' },
              { metric: 'Cycle Life', lithium: '1,500-2,000', multiIon: '5,000+', winner: 'multiIon' },
              { metric: 'Temp Range', lithium: '0°C to 45°C', multiIon: '-40°C to 65°C', winner: 'multiIon' },
              { metric: 'Safety', lithium: 'Requires BMS', multiIon: 'Inherently stable', winner: 'multiIon' },
            ].map((item, index) => (
              <div key={index} className="bg-gray-900/50 rounded-xl p-4 border border-white/5">
                <p className="text-gray-500 text-xs font-semibold uppercase mb-3">{item.metric}</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">Li-Ion</span>
                    <span className="text-amber-400/70 text-sm">{item.lithium}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-400 text-xs font-semibold">Multi-Ion</span>
                    <span className="text-emerald-400 font-bold text-sm">{item.multiIon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowMultiIonWorksSection;
